/**
 * @file        src/pages/hotel/MarkupList.jsx
 * @description B2C Hotel Markup List (margin_rules, flow_type='hotel'). Legacy
 *              parity columns: # / Amount / Amount Type / Start Range / End Range
 *              / Updated On / Action (Delete · Edit). Add + edit live on the
 *              AddMarkup form page.
 * @author      Kinetika Labs
 * @created     2026-07-02
 * @changelog
 *   - 2026-07-02 Claude — Initial (B2C Hotel Management).
 *   - 2026-07-12 Claude — Legacy parity columns (Amount / Amount Type / Start
 *                          Range / End Range). "percent" / "fix" match the
 *                          margin_rules ENUM ('percentage' / 'fixed'); an inactive
 *                          rule is chipped inline rather than given its own column.
 *   - 2026-08-03 Claude — Brand column (registry name for the row's 3-char code).
 *   - 2026-09-12 Claude — Precedence notice. The API already rejects a rule whose
 *                          price range overlaps another active one, and the engine
 *                          picks the band containing the stay's price (most
 *                          recently updated wins a tie) — but neither was stated
 *                          anywhere on this screen.
 *   - 2026-09-15 Claude — Note now also states that the markup is already inside
 *                          the displayed price, so the convenience fee, discounts
 *                          and coupons are all computed on the marked-up total —
 *                          never on the supplier net.
 *   - 2026-09-25 Claude — `keepPreviousData: true` is a React Query **v4** option
 *                          and this workspace is on v5 (5.101), where no such
 *                          option exists and it was silently ignored. So every
 *                          page / filter / search change dropped `data` to
 *                          undefined, the table was swapped for six skeleton rows
 *                          and the panel collapsed ~700px before growing back —
 *                          the "pages flicker / the page moves" report. v5 spells
 *                          it `placeholderData: keepPreviousData` (a FUNCTION
 *                          imported from the package, not a boolean).
 *   - 2026-10-06 Claude — "Dom / Int" column (the margin_rules `dom_int` the
 *                          hotel screen now writes).
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { keepPreviousData, useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import PageHead from '../../components/PageHead';
import { MARKUP_BASE_NOTE } from '../../lib/pricingNotes';
import Icon from '../../components/Icon';
import Chip from '../../components/Chip';
import Pager from '../../components/Pager';
import TableSkeleton from '../../components/TableSkeleton';
import { useBrands } from '../../api/brands';
import { get, del } from '../../api/client';
import { toast } from '../../lib/toast';
import { confirm } from '../../lib/confirm';
import { money, dateTime } from './cells';
import { domIntLabel } from '../../lib/domInt';

export default function MarkupList() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { labelOf } = useBrands();
  const [page, setPage] = useState(1);
  const pageSize = 25;

  const params = { page, pageSize, audience: 'b2c' };
  const { data, isLoading } = useQuery({
    queryKey: ['hotel-markups', 'list', params],
    queryFn: () => get('/hotel-markups', { params }),
    placeholderData: keepPreviousData,
  });
  const rows = data?.data || [];
  const total = data?.meta?.total || 0;

  const removeMut = useMutation({
    mutationFn: (id) => del(`/hotel-markups/${id}`),
    onSuccess: () => {
      toast.ok('Deleted', 'Markup removed.');
      qc.invalidateQueries({ queryKey: ['hotel-markups'] });
    },
    onError: (err) => toast.err('Failed', err.response?.data?.error?.message || 'Could not delete'),
  });

  async function onDelete(row) {
    if (!(await confirm(`Delete markup #${row.id}?`))) return;
    removeMut.mutate(row.id);
  }

  const isPct = (r) => r.margin_type === 'percentage';

  return (
    <>
      <PageHead
        title="B2C Hotel Markup List"
        subtitle="B2C Hotel Management"
        actions={<button className="btn primary" onClick={() => navigate('/hotel/markups/new')}><Icon name="plus" /> Add B2C Markup</button>}
      />

      <div className="notice-banner info">
        <span>
          Only <b>one</b> markup applies to a stay: the rule whose price range contains
          the stay&apos;s price; then a <b>Domestic- or International-only</b> rule beats
          one for both (a stay is domestic when the hotel is in India); the most
          recently updated wins a tie. A rule whose range <b> overlaps</b> another active
          rule on the same brand and Domestic/International choice is rejected — edit
          the existing rule instead.
          {MARKUP_BASE_NOTE}
        </span>
      </div>

      <article className="panel glass">
        <div className="table-wrap">
          {isLoading ? <TableSkeleton /> : (
            <table className="table">
              <thead>
                <tr><th>#</th><th>Brand</th><th>Amount</th><th>Amount Type</th><th>Start Range</th><th>End Range</th><th>Dom / Int</th><th>Updated On</th><th>Action</th></tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr><td colSpan={9} className="empty">No hotel markup rules yet. Add your first one.</td></tr>
                ) : rows.map((r, idx) => (
                  <tr key={r.id}>
                    <td className="mono">{(page - 1) * pageSize + idx + 1}</td>
                    <td>{labelOf(r.brand)}</td>
                    <td><b>{r.margin_value}</b></td>
                    <td>
                      {isPct(r) ? 'percent' : 'fix'}
                      {!r.is_active ? <> <Chip tone="rej">Inactive</Chip></> : null}
                    </td>
                    <td>{r.price_min != null ? money(r.price_min) : '—'}</td>
                    <td>{r.price_max != null ? money(r.price_max) : '—'}</td>
                    <td>{domIntLabel(r.dom_int)}</td>
                    <td className="muted">{dateTime(r.updated_at)}</td>
                    <td className="row-act">
                      <button className="ib del" aria-label="Delete" onClick={() => onDelete(r)}><Icon name="trash" /></button>
                      <button className="ib edit" aria-label="Edit" onClick={() => navigate(`/hotel/markups/${r.id}/edit`)}><Icon name="edit" /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <Pager page={page} pageSize={pageSize} total={total} onPage={setPage} />
      </article>
    </>
  );
}
